// components/DualLogin.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom'; // Add this for redirect
import './Auth.css';

const DualLogin = () => {
  const [mode, setMode] = useState('login'); // login or signup
  const [loginMethod, setLoginMethod] = useState('phone'); // phone or email
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState(''); // 'success' or 'error'
  
  const navigate = useNavigate();
  
  const { 
    signup,                    // This is the signup function
    login,                     // This is email login
    loginWithPhone,            // This is phone login
    error: authError, 
    setError 
  } = useAuth();

  // Simple password length check
  const passwordLength = password.length;
  const charsNeeded = Math.max(0, 8 - passwordLength);
  const isPasswordValid = passwordLength >= 8;

  // Show notification function
  const showNotificationMessage = (message, type) => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    
    // Auto hide after 3 seconds
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  // Format Ugandan phone number for display as user types
  const formatPhoneForDisplay = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    
    // Format as user types (Ugandan style: 0704 804 475)
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
    } else if (cleaned.length <= 9) {
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
    } else {
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 10)}`;
    }
  };

  // Convert local Ugandan number to international format (+256...)
  const convertToInternational = (localNumber) => {
    // Remove all non-digits
    const cleaned = localNumber.replace(/\D/g, '');
    
    // Ugandan numbers: if starts with 0, replace with +256
    if (cleaned.startsWith('0')) {
      return `+256${cleaned.slice(1)}`;
    }
    // If already has country code (256), add +
    else if (cleaned.startsWith('256')) {
      return `+${cleaned}`;
    }
    // If just the number without 0 (704804475), add +256
    else if (cleaned.length === 9) {
      return `+256${cleaned}`;
    }
    // Default: assume it's already international
    else if (cleaned.startsWith('+')) {
      return cleaned;
    }
    else {
      return `+${cleaned}`;
    }
  };

  // Handle phone input change
  const handlePhoneChange = (e) => {
    const rawValue = e.target.value;
    const cleaned = rawValue.replace(/\D/g, '');
    setPhoneNumber(cleaned);
  };

  // Get display value for input
  const getDisplayPhone = () => {
    return formatPhoneForDisplay(phoneNumber);
  };

  // Handle login with phone + password
  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || phoneNumber.length < 9) {
      setLocalError('Please enter a valid Ugandan phone number (e.g., 0704804475)');
      return;
    }

    if (!password) {
      setLocalError('Please enter your password');
      return;
    }

    const internationalPhone = convertToInternational(phoneNumber);

    try {
      setLoading(true);
      setLocalError('');
      setError('');
      
      await loginWithPhone(internationalPhone, password);
      
      // Show success notification
      showNotificationMessage('✓ Login successful! Redirecting...', 'success');
      
      // Redirect to main app
      setTimeout(() => {
        navigate('/trackmed2');
      }, 1000);
      
    } catch (err) {
      console.error('Login error:', err);
      showNotificationMessage(err.message || 'Login failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle login with email + password
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setLocalError('Please enter your email');
      return;
    }

    if (!password) {
      setLocalError('Please enter your password');
      return;
    }

    try {
      setLoading(true);
      setLocalError('');
      setError('');
      
      await login(email, password);
      
      // Show success notification
      showNotificationMessage('✓ Login successful! Redirecting...', 'success');
      
      // Redirect to main app
      setTimeout(() => {
        navigate('/trackmed2');
      }, 1000);
      
    } catch (err) {
      console.error('Login error:', err);
      showNotificationMessage(err.message || 'Login failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle signup with phone + password (NO EMAIL)
  const handlePhoneSignup = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber || phoneNumber.length < 9) {
      setLocalError('Please enter a valid Ugandan phone number (e.g., 0704804475)');
      return;
    }

    if (!displayName.trim()) {
      setLocalError('Please enter your name');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    const internationalPhone = convertToInternational(phoneNumber);

    try {
      setLoading(true);
      setLocalError('');
      setError('');
      
      // For phone signup, we need to create a placeholder email
      const placeholderEmail = `${phoneNumber}@phone.user`;
      await signup(placeholderEmail, password, displayName, internationalPhone);
      
      showNotificationMessage('✓ Account created successfully!', 'success');
      
      // Redirect to main app
      setTimeout(() => {
        navigate('/trackmed2');
      }, 1500);
      
    } catch (err) {
      console.error('Signup error:', err);
      showNotificationMessage(err.message || 'Signup failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle signup with email + password (NO PHONE)
  const handleEmailSignup = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setLocalError('Please enter your email');
      return;
    }

    if (!displayName.trim()) {
      setLocalError('Please enter your name');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setLocalError('');
      setError('');
      
      // For email signup, pass empty phone number
      await signup(email, password, displayName, '');
      
      showNotificationMessage('✓ Account created successfully!', 'success');
      
      // Redirect to main app
      setTimeout(() => {
        navigate('/trackmed2');
      }, 1500);
      
    } catch (err) {
      console.error('Signup error:', err);
      showNotificationMessage(err.message || 'Signup failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        {/* Notification Popup */}
        {showNotification && (
          <div className={`auth-notification ${notificationType}`}>
            {notificationMessage}
          </div>
        )}

        <div className="auth-header">
          <h2>
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p>
            {mode === 'login' 
              ? 'Login with your phone or email' 
              : 'Sign up to start tracking your medications'}
          </p>
        </div>

        {(localError || authError) && !showNotification && (
          <div className="auth-error">
            {localError || authError}
          </div>
        )}

        {/* LOGIN SCREEN */}
        {mode === 'login' && (
          <div className="auth-form">
            {/* Method Toggle */}
            <div className="method-toggle">
              <button
                type="button"
                className={`method-btn ${loginMethod === 'phone' ? 'active' : ''}`}
                onClick={() => setLoginMethod('phone')}
              >
                📱 Use Phone
              </button>
              <button
                type="button"
                className={`method-btn ${loginMethod === 'email' ? 'active' : ''}`}
                onClick={() => setLoginMethod('email')}
              >
                ✉️ Use Email
              </button>
            </div>

            {/* Phone Login Form */}
            {loginMethod === 'phone' && (
              <form onSubmit={handlePhoneLogin}>
                <div className="form-group">
                  <label htmlFor="phoneNumber">Ugandan Phone Number</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={getDisplayPhone()}
                    onChange={handlePhoneChange}
                    placeholder="0704 804 475"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                  <small className="input-hint">Enter your number without the country code</small>
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                </div>

                <button type="submit" className="auth-button" disabled={loading}>
                  {loading ? 'Please wait...' : 'Login with Phone'}
                </button>
              </form>
            )}

            {/* Email Login Form */}
            {loginMethod === 'email' && (
              <form onSubmit={handleEmailLogin}>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                </div>

                <button type="submit" className="auth-button" disabled={loading}>
                  {loading ? 'Please wait...' : 'Login with Email'}
                </button>
              </form>
            )}

            <div className="auth-footer">
              <p>
                Don't have an account?{' '}
                <button onClick={() => setMode('signup')} className="auth-link" disabled={loading}>
                  Sign Up
                </button>
              </p>
            </div>
          </div>
        )}

        {/* SIGNUP SCREEN */}
        {mode === 'signup' && (
          <div className="auth-form">
            {/* Method Toggle */}
            <div className="method-toggle">
              <button
                type="button"
                className={`method-btn ${loginMethod === 'phone' ? 'active' : ''}`}
                onClick={() => setLoginMethod('phone')}
              >
                📱 Sign up with Phone
              </button>
              <button
                type="button"
                className={`method-btn ${loginMethod === 'email' ? 'active' : ''}`}
                onClick={() => setLoginMethod('email')}
              >
                ✉️ Sign up with Email
              </button>
            </div>

            {/* Phone Signup Form - NO EMAIL FIELD */}
            {loginMethod === 'phone' && (
              <form onSubmit={handlePhoneSignup}>
                <div className="form-group">
                  <label htmlFor="signup-phone">Ugandan Phone Number</label>
                  <input
                    type="tel"
                    id="signup-phone"
                    value={getDisplayPhone()}
                    onChange={handlePhoneChange}
                    placeholder="0704 804 475"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                  <small className="input-hint">Example: 0704804475 (MTN, Airtel, etc.)</small>
                </div>

                <div className="form-group">
                  <label htmlFor="signup-name">Full Name</label>
                  <input
                    type="text"
                    id="signup-name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your name"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="signup-password">Password</label>
                  <input
                    type="password"
                    id="signup-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    placeholder="Create a password (min. 8 characters)"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                  
                  {/* Simple password length indicator */}
                  {(passwordFocused || password.length > 0) && (
                    <div className="password-length-indicator">
                      <div className="length-bar">
                        <div 
                          className="length-fill"
                          style={{ 
                            width: `${Math.min(100, (passwordLength / 8) * 100)}%`,
                            backgroundColor: isPasswordValid ? '#10b981' : '#ef4444'
                          }}
                        ></div>
                      </div>
                      <div className="length-text" style={{ color: isPasswordValid ? '#10b981' : '#ef4444' }}>
                        {isPasswordValid 
                          ? '✓ Password strength: Good' 
                          : `${charsNeeded} more character${charsNeeded !== 1 ? 's' : ''} needed`}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="signup-confirm-password">Confirm Password</label>
                  <input
                    type="password"
                    id="signup-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                  {confirmPassword && confirmPassword !== password && (
                    <div className="password-mismatch">Passwords do not match</div>
                  )}
                </div>

                <button type="submit" className="auth-button" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Sign Up with Phone'}
                </button>
              </form>
            )}

            {/* Email Signup Form - NO PHONE FIELD */}
            {loginMethod === 'email' && (
              <form onSubmit={handleEmailSignup}>
                <div className="form-group">
                  <label htmlFor="signup-email2">Email Address</label>
                  <input
                    type="email"
                    id="signup-email2"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="signup-name2">Full Name</label>
                  <input
                    type="text"
                    id="signup-name2"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your name"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="signup-password2">Password</label>
                  <input
                    type="password"
                    id="signup-password2"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    placeholder="Create a password (min. 8 characters)"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                  
                  {/* Simple password length indicator */}
                  {(passwordFocused || password.length > 0) && (
                    <div className="password-length-indicator">
                      <div className="length-bar">
                        <div 
                          className="length-fill"
                          style={{ 
                            width: `${Math.min(100, (passwordLength / 8) * 100)}%`,
                            backgroundColor: isPasswordValid ? '#10b981' : '#ef4444'
                          }}
                        ></div>
                      </div>
                      <div className="length-text" style={{ color: isPasswordValid ? '#10b981' : '#ef4444' }}>
                        {isPasswordValid 
                          ? '✓ Password strength: Good' 
                          : `${charsNeeded} more character${charsNeeded !== 1 ? 's' : ''} needed`}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="signup-confirm-password2">Confirm Password</label>
                  <input
                    type="password"
                    id="signup-confirm-password2"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="auth-input"
                    required
                    disabled={loading}
                  />
                  {confirmPassword && confirmPassword !== password && (
                    <div className="password-mismatch">Passwords do not match</div>
                  )}
                </div>

                <button type="submit" className="auth-button" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Sign Up with Email'}
                </button>
              </form>
            )}

            <div className="auth-footer">
              <p>
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="auth-link" disabled={loading}>
                  Login
                </button>
              </p>
            </div>
          </div>
        )}

        {/* <div id="recaptcha-container"></div> */}
      </div>
    </div>
  );
};

export default DualLogin;